<template>
	<v-navigation-drawer
		app
		temporary
		right
		hide-overlay
		v-model="showDrawer"
		width="350px"
	>
		<v-card class="mx-auto card" height="100%">
			<v-card-text>
				<v-row>
					<v-col>
						<div class="title-details">
							{{ $t("details") }}
						</div>
					</v-col>
					<v-col>
						<v-btn
							color="#0d5ae5"
							text
							absolute
							top
							right
							@click="showDrawer = false"
							class="mt-1"
						>
							X
						</v-btn>
					</v-col>
				</v-row>
				<compress-string
					:name="project.projectName"
					:widthAvailable="18"
					inClass="display-1 text--primary mb-0 mt-2"
				/>
			</v-card-text>
			<v-divider class="px-5" />
			<v-card-text>
				<p class="text--secondary">
					{{ buildDescription }}
				</p>
			</v-card-text>
			<map-display :markers="this.project.extractions" />

			<v-container>
				<v-row>
					<v-col
						class="subtitle-2 subheader"
						v-if="project.description.length !== 0"
					>
						{{ $t("description") }}
					</v-col>
				</v-row>
				<v-row>
					<v-col>
						<span class="data">{{ project.description }}</span>
					</v-col>
				</v-row>
				<v-row>
					<v-col class="subtitle-2 subheader">
						<span>{{ $t("submittedBy") }}</span>
					</v-col>
					<v-col class="subtitle-2 subheader">
						<span>{{ $t("expirationDate") }}</span>
					</v-col>
				</v-row>
				<v-row>
					<v-col>
						<span class="data">{{ project.startedBy }}</span>
					</v-col>
					<v-col>
						<ExpirationCounter :date="project.expirationDate" class="data" />
					</v-col>
				</v-row>
				<v-row>
					<v-col class="subtitle-2 subheader">
						{{ $t("submittedDate") }}
					</v-col>
					<v-col class="subtitle-2 subheader">
						{{ $t("size") }}
					</v-col>
				</v-row>
				<v-row>
					<v-col>
						<span class="data">{{ creationString }}</span>
					</v-col>
					<v-col>
						<span class="data" v-if="project.size !== 0">{{
							sizeToString
						}}</span>
						<span v-if="project.size === 0">-</span>
					</v-col>
				</v-row>

				<span>
					<v-row>
						<v-col class="subtitle-2 subheader">
							{{ $t("status") }}
						</v-col>
						<v-col
							class="subtitle-2 subheader"
							v-if="project.organismName !== null"
						>
							{{ $t("organism") }}
						</v-col>
					</v-row>
					<v-row>
						<v-col>
							<status-box
								:status="project.status"
								:projectId="project.id"
								:isProgress="true"
								:progressValue="project.progress"
							/>
						</v-col>
						<v-col v-if="project.organismName !== null">
							<p class="pa-0 ma-0">
								<span class="data">{{ project.organismName }}</span>
							</p>
						</v-col>
					</v-row>
					<div>
						<v-row>
							<v-col
								class="subtitle-2 subheader"
								v-if="
									project.status === 'Approved' || project.status === 'Denied'
								"
							>
								{{ $t("lastAuditor") }}
							</v-col>
							<v-col
								class="subtitle-2 subheader"
								v-if="project.organismName === null"
							>
								{{ $t("projectDownloaders") }}
							</v-col>
						</v-row>
						<v-row>
							<v-col
								v-if="
									project.status === 'Approved' || project.status === 'Denied'
								"
							>
								<span class="data">{{ project.statusLogs.changedBy }}</span>
							</v-col>
							<v-col v-if="project.organismName === null">
								<p v-for="dn in downloaders" v-bind:key="dn" class="pa-0 ma-0">
									<span class="data">{{ dn }}</span>
								</p>
							</v-col>
						</v-row>
						<div
							v-if="
								project.status === 'Denied' &&
									project.statusLogs.additionalInfo &&
									project.statusLogs.additionalInfo.length > 0
							"
						>
							<v-row>
								<v-col class="subtitle-2 subheader">
									{{ $t("auditReason") }}
								</v-col>
							</v-row>
							<v-row>
								<v-col>
									<span class="data">{{
										project.statusLogs.additionalInfo
									}}</span>
								</v-col>
							</v-row>
						</div>
					</div>
				</span>
			</v-container>
			<v-card-actions class="buttons" v-if="canAudit()">
				<v-btn
					text
					color="#5f98ed"
					dark
					@click.stop="auditResult(true)"
					:disabled="
						project.status === 'PreApproved' || project.status === 'Approved'
					"
				>
					{{ $t("audit.preApprove") }}
				</v-btn>
				<v-btn
					text
					color="#5f98ed"
					dark
					@click.stop="auditResult(false)"
					:disabled="
						project.status === 'Denied' || project.status === 'Approved'
					"
				>
					{{ $t("audit.deny") }}
				</v-btn>
			</v-card-actions>
			<v-card-actions class="buttons" v-if="canLegalAudit()">
				<v-btn
					text
					color="#5f98ed"
					@click.stop="legalAuditResult(true)"
					:disabled="
						project.status === 'Approved' || project.status === 'Finished'
					"
				>
					{{ $t("audit.approve") }}
				</v-btn>
				<v-btn
					text
					color="#5f98ed"
					@click.stop="legalAuditResult(false)"
					:disabled="project.status === 'Denied'"
				>
					{{ $t("audit.deny") }}
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-navigation-drawer>
</template>

<script>
import PermissionMixin from "@/mixins/PermissionMixin";

export default {
	mixins: [PermissionMixin],
	props: {
		project: {
			type: Object,
			default: () => {
				return {
					name: "",
				};
			},
		},
		drawerProp: {
			type: Boolean,
			default: false,
		},
	},
	components: {
		ExpirationCounter: () => import("./ExpirationCounter"),
		MapDisplay: () => import("@/components/base/Map"),
		CompressString: () => import("@/components/base/CompressString"),
	},
	data() {
		return {
			drawer: false,
		};
	},
	mounted() {
		this.drawer = this.drawerProp;
	},
	computed: {
		showDrawer: {
			get() {
				return this.drawer;
			},
			set(value) {
				this.drawer = value;
			},
		},
		creationString: {
			get() {
				var aux = new Date(this.project.creationDate);
				return new Date(aux.toString() + " UTC").toLocaleString();
			},
		},
		sizeToString: {
			get() {
				return this.returnSize(this.project.size);
			},
		},
		downloaders: {
			get() {
				return this.project.downloaders;
			},
		},
		buildDescription: {
			get() {
				var res = this.$tc("projectCameraSummary", this.project.cameras, {
					cameras: this.project.cameras,
				});
				var time = this.project.hours.toFixed(2);
				if (time < 1) {
					time = Math.floor(time * 60);
					res =
						res +
						" " +
						this.$tc("projectHoursSummary", time, { time: time }) +
						" " +
						this.$tc("minute", time);
				} else {
					time = Math.floor(time);
					res =
						res +
						" " +
						this.$tc("projectHoursSummary", time, { time: time }) +
						" " +
						this.$tc("hour", time);
				}
				return res;
			},
		},
	},
	methods: {
		canAudit() {
			if (this.$store.getters.getRole !== "admin") {
				return (
					this.userCanAudit &&
					this.project &&
					(this.project.status === "Finished" ||
						this.project.status === "PreApproved" ||
						this.project.status === "Denied")
				);
			} else {
				if (
					this.project.status === "Finished" ||
					this.project.status === "Denied"
				) {
					return true;
				} else {
					return false;
				}
			}
		},
		canLegalAudit() {
			return (
				this.userCanLegalAudit &&
				this.project &&
				(this.project.status === "PreApproved" ||
					this.project.status === "Approved")
			);
		},
		returnSize(value) {
			var a = value / 1000000;
			if (a < 1000) {
				return a.toFixed(2).toString() + " MB";
			} else {
				return (a / 1000).toFixed(2).toString() + " GB";
			}
		},
		auditResult(value) {
			this.$store.dispatch("setOnDialog", {
				audit: true,
				isLegalAudit: false,
				result: value,
				project: this.$store.getters.getProjectRaw,
			});
		},
		legalAuditResult(value) {
			this.$store.dispatch("setOnDialog", {
				audit: true,
				isLegalAudit: true,
				result: value,
				project: this.$store.getters.getProjectRaw,
			});
		},
	},
};
</script>

<style>
.detailsHeader {
	color: blue;
	background-color: black;
}
.subheader {
	padding-bottom: 0px;
}
.card {
	box-shadow: none !important;
}
.title-details {
	font-size: 18px;
	font-family: "Roboto", "sans-serif";
	color: #0d5ae5;
}
.data {
	font-family: "Roboto", "sans-serif";
	font-size: 14px;
	color: #91919c;
	font-weight: bold;
}
.v-btn__content {
	font-size: 14px !important;
}
.buttons {
	justify-content: space-around !important;
	font-size: 14px !important;
}
.btn::before {
	background-color: transparent !important;
}
.btn:hover {
	color: #4780d6 !important;
}
</style>
